import { Fragment, useMemo, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ChevronDown } from "lucide-react";
import { GetVendorUomMMapping, VendorUomMMapping } from "../../../../redux/actions/settings-action";
import { useDispatch } from "react-redux";

const SearchableDropdown = ({ value, label, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return options
      .filter((o) =>
        o.alt_uom?.toLowerCase().includes(query.toLowerCase())
      )
      .slice(0, 5);
  }, [options, query]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-[34px] w-full rounded-[6px] border border-[#E6EAF2] px-3 pr-8 text-left text-[12px] bg-white"
      >
        {label || "System UOM"}
        <span className="absolute right-2 top-1/2 -translate-y-1/2">
          <ChevronDown size={14} />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-[6px] border border-[#E6EAF2] bg-white shadow-md">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            className="w-full border-b border-[#E6EAF2] px-3 py-2 text-[12px] outline-none"
          />

          <div className="max-h-[180px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-[12px] text-[#6B7280]">
                No results
              </div>
            ) : (
              filtered.map((o) => (
                <div
                  key={o.item_id}
                  onClick={() => {
                    onChange(o);          // pass full object
                    setOpen(false);
                    setQuery("");
                  }}
                  className="cursor-pointer px-3 py-2 text-[12px] hover:bg-[#F3F5F9]"
                >
                  {o.alt_uom}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* =====================================================
   SelectUOMSidePanel
===================================================== */

const SelectUOMSidePanel = ({
  open = false,
  setOpen,
  uom = [],
  selectedVendorUOMS = [],
  uomMemo = {},
  setUomMemo,
  onVendorUomsChange = () => {},
}) => {
  const dispatch = useDispatch();

  // When modal opens or selectedVendorUOMS changes, fetch existing mappings for any vendor_uom
  useEffect(() => {
    if (!open || !selectedVendorUOMS || selectedVendorUOMS.length === 0) return;
    // find vendor_uoms not present in memo
    const missing = selectedVendorUOMS.filter((vu) => !uomMemo || !uomMemo[vu]);
    if (missing.length === 0) return;

    dispatch(
      GetVendorUomMMapping(
        { vendor_uoms: missing },
        (res) => {
          const rows = res?.data || [];
          if (rows.length === 0) {
            // attempt to rematch vendor_uom to existing system UOM options (by name)
            setUomMemo((prev) => {
              const next = { ...(prev || {}) };
              missing.forEach((vu) => {
                if (next[vu]) return;
                const match = (uom || []).find(
                  (o) => (o.alt_uom || "").toString().toLowerCase() === vu.toString().toLowerCase()
                );
                if (match) {
                  next[vu] = { item_id: match.item_id, uom_mapping_id: null, uom_name: match.alt_uom || "" };
                } else {
                  next[vu] = { item_id: null, uom_mapping_id: null, uom_name: "" };
                }
              });
              return next;
            });
            return;
          }

          // update memo from API response (expected: vendor_uom, mapped_uom_id, alt_uom)
          setUomMemo((prev) => {
            const next = { ...(prev || {}) };
            rows.forEach((r) => {
              const vendor_uom = r.vendor_uom;
              const mapped = r.mapped_uom_id;
              const alt = r.alt_uom || "";
              // set mapping_id null for GetVendor response (we'll set it after explicit VendorUomMMapping save)
              next[vendor_uom] = { item_id: mapped || null, uom_mapping_id: r.uom_mapping_id ?? null, uom_name: alt };
            });
            // ensure any still-missing entries are present
            missing.forEach((vu) => {
              if (!next[vu]) next[vu] = { item_id: null, uom_mapping_id: null, uom_name: "" };
            });
            return next;
          });
        },
        () => {
          // on error: initialize missing as unmapped
          setUomMemo((prev) => {
            const next = { ...(prev || {}) };
            missing.forEach((vu) => {
              if (!next[vu]) next[vu] = { item_id: null, uom_mapping_id: null, uom_name: "" };
            });
            return next;
          });
        }
      )
    );
  }, [open, selectedVendorUOMS]);

  /* -----------------------------------------------
     Rows derived from vendor UOMs + memo
  ------------------------------------------------ */
  const [localRows, setLocalRows] = useState([]);

  useEffect(() => {
    if (!open) return;
    // initialize rows from selectedVendorUOMS
    const init = (selectedVendorUOMS || []).map((vu, idx) => ({ id: `${vu}_${idx}`, vendor_uom: vu }));
    // ensure at least one blank row
    if (init.length === 0) init.push({ id: `new_${Date.now()}`, vendor_uom: "" });
    setLocalRows(init);
    // notify parent of initial vendor uoms
    const initialVendorUoms = init.map((r) => r.vendor_uom).filter(Boolean);
    onVendorUomsChange(initialVendorUoms);
  }, [open, selectedVendorUOMS]);

  const rows = useMemo(() => {
    return (localRows || []).map((r) => {
      const memo = uomMemo[r.vendor_uom];
      return {
        id: r.id,
        vendor_uom: r.vendor_uom,
        system_uom: memo?.item_id ?? null,
        system_uom_name: memo?.uom_name ?? "",
      };
    });
  }, [localRows, uomMemo]);

  const updateRowValue = (id, value) => {
    setLocalRows((prev) => {
      const next = prev.map((r) => (r.id === id ? { ...r, vendor_uom: value } : r));
      const vendorUoms = next.map((r) => r.vendor_uom).filter((v) => v && v.toString().trim());
      onVendorUomsChange(vendorUoms);
      return next;
    });
  };

  const addRowAfter = (id) => {
    setLocalRows((prev) => {
      const idx = prev.findIndex((r) => r.id === id);
      const next = [...prev];
      const newRow = { id: `new_${Date.now()}`, vendor_uom: "" };
      if (idx === -1) next.push(newRow);
      else next.splice(idx + 1, 0, newRow);
      const vendorUoms = next.map((r) => r.vendor_uom).filter((v) => v && v.toString().trim());
      onVendorUomsChange(vendorUoms);
      return next;
    });
  };

  const removeRow = (id) => {
    setLocalRows((prev) => {
      const next = prev.filter((r) => r.id !== id);
      const final = next.length ? next : [{ id: `new_${Date.now()}`, vendor_uom: "" }];
      const vendorUoms = final.map((r) => r.vendor_uom).filter((v) => v && v.toString().trim());
      onVendorUomsChange(vendorUoms);
      return final;
    });
  };

  const fetchVendorUomMappingFor = (vendor_uom) => {
    if (!vendor_uom || uomMemo[vendor_uom]) return;
    dispatch(
      GetVendorUomMMapping(
        { vendor_uoms: [vendor_uom] },
        (res) => {
          const rows = res?.data || [];
          if (!rows || rows.length === 0) {
            const match = (uom || []).find(
              (o) => (o.alt_uom || "").toString().toLowerCase() === vendor_uom.toString().toLowerCase()
            );
            setUomMemo((prev) => ({ ...(prev || {}), [vendor_uom]: match ? { item_id: match.item_id, uom_mapping_id: null, uom_name: match.alt_uom } : { item_id: null, uom_mapping_id: null, uom_name: "" } }));
            return;
          }
          setUomMemo((prev) => {
            const next = { ...(prev || {}) };
            rows.forEach((r) => {
              const vu = r.vendor_uom;
              next[vu] = { item_id: r.mapped_uom_id || null, uom_mapping_id: r.uom_mapping_id || null, uom_name: r.alt_uom || "" };
            });
            return next;
          });
        },
        () => {
          setUomMemo((prev) => ({ ...(prev || {}), [vendor_uom]: { item_id: null, uom_mapping_id: null, uom_name: "" } }));
        }
      )
    );
  };

  /* -----------------------------------------------
     Stub: resolve mapping (API later)
  ------------------------------------------------ */

  const resolveUomMapping = (vendor_uom, item_id) => {
    console.log("resolveUomMapping:", vendor_uom, item_id);

    // simulate backend response
    return {
      item_id,
      uom_mapping_id: Date.now(),
    };

    // return null; // simulate "do nothing"
  };

  /* -----------------------------------------------
     Handle dropdown change
  ------------------------------------------------ */

  const handleSystemUomChange = (vendor_uom, uomObj) => {
    const { item_id, alt_uom } = uomObj;

    const existing = uomMemo[vendor_uom];

    if (existing) {
      // user changed selection -> mapping relation (uom_mapping_id) should be cleared
      setUomMemo((prev) => ({
        ...prev,
        [vendor_uom]: {
          ...prev[vendor_uom],
          item_id,
          uom_name: alt_uom,
          uom_mapping_id: null,
        },
      }));
      return;
    }

    const result = resolveUomMapping(vendor_uom, item_id);
    if (!result) return;

    // If there's no existing memo, treat this as a local selection and mark mapping_id null
    setUomMemo((prev) => ({
      ...prev,
      [vendor_uom]: {
        item_id,
        uom_mapping_id: null,
        uom_name: alt_uom,
      },
    }));
  };

  /* -----------------------------------------------
     Save
  ------------------------------------------------ */

  const handleSave = () => {
    // delegate to persistMappingsIfNeeded and close on success
    persistMappingsIfNeeded()
      .then(() => setOpen(false))
      .catch((err) => {
        console.error("VendorUomMMapping failed", err);
      });
  };

  const persistMappingsIfNeeded = () => {
    return new Promise((resolve, reject) => {
      const toMap = Object.keys(uomMemo || {})
        .filter((vu) => uomMemo[vu]?.item_id && !uomMemo[vu]?.uom_mapping_id)
        .map((vu) => ({ vendor_uom: vu, mappedUomIds: [uomMemo[vu].item_id] }));

      if (toMap.length === 0) return resolve();

      dispatch(
        VendorUomMMapping(
          { data: toMap },
          (res) => {
            const rows = res?.data || [];
            setUomMemo((prev) => {
              const next = { ...(prev || {}) };
              rows.forEach((r) => {
                const vu = r.vendor_uom;
                const mapped = r.mapped_uom_id;
                const mappingId = r.uom_mapping_id;
                const opt = (uom || []).find((o) => o.item_id === mapped);
                const alt = opt?.alt_uom || next[vu]?.uom_name || "";
                next[vu] = { ...(next[vu] || {}), item_id: mapped, uom_mapping_id: mappingId, uom_name: alt };
              });
              return next;
            });
            resolve(res);
          },
          (err) => {
            reject(err);
          }
        )
      );
    });
  };

  /* -----------------------------------------------
     UI
  ------------------------------------------------ */

  return (
    <Transition.Root show={!!open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <div className="fixed inset-0 overflow-hidden">
          <Dialog.Overlay
            className="absolute inset-0 bg-[#0000004D]"
            onClick={async () => {
              // overlay click should trigger mapping before close
              try {
                await persistMappingsIfNeeded();
                setOpen(false);
              } catch (err) {
                console.error("VendorUomMMapping failed on overlay", err);
              }
            }}
          />

          <div className="fixed inset-0 flex justify-end">
            <Dialog.Panel className="relative h-full w-[400px] sm:w-[520px] bg-white rounded-tl-lg rounded-bl-lg shadow-xl flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-[#E6EAF2] flex justify-between">
                <div>
                  <Dialog.Title className="text-[16px] font-semibold">
                    Select UOM
                  </Dialog.Title>
                  <div className="text-[12px] text-[#6B7280]">
                    Map vendor UOM to system UOM
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await persistMappingsIfNeeded();
                      setOpen(false);
                    } catch (err) {
                      console.error("VendorUomMMapping failed on close", err);
                    }
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Column headers */}
              <div className="px-5 pt-4">
                <div className="grid grid-cols-[1fr_24px_1fr_24px] text-[12px] font-semibold">
                  <div>Vendor UOMs</div>
                  <div />
                  <div>System UOMs</div>
                  <div />
                </div>
              </div>

              {/* Rows */}
              <div className="px-5 py-4 flex-1 overflow-y-auto">
                <div className="flex flex-col gap-3">
                  {rows.map((r, idx) => (
                    <div key={r.id} className="grid grid-cols-[1fr_24px_1fr_24px] items-center gap-2">
                      {/* Vendor UOM (editable) */}
                      <div className="h-[34px]">
                        <input
                          value={r.vendor_uom || ""}
                          onChange={(e) => updateRowValue(r.id, e.target.value)}
                          onBlur={(e) => fetchVendorUomMappingFor(e.target.value)}
                          placeholder="Enter UOM"
                          className="w-full h-[34px] rounded-[6px] border border-[#E6EAF2] px-3 text-[12px] bg-white"
                        />
                      </div>

                      <div className="text-center">→</div>

                      {/* System UOM */}
                      <SearchableDropdown
                        value={r.system_uom}
                        label={r.system_uom_name}
                        options={uom}
                        onChange={(uomObj) => handleSystemUomChange(r.vendor_uom, uomObj)}
                      />

                      <div className="flex items-center justify-center">
                        {idx === rows.length - 1 ? (
                          <button
                            type="button"
                            className="text-primary text-[18px] font-bold"
                            onClick={() => addRowAfter(r.id)}
                            title="Add UOM"
                          >
                            +
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="text-red-500 text-[18px] font-bold"
                            onClick={async () => {
                              try {
                                await persistMappingsIfNeeded();
                                removeRow(r.id);
                              } catch (err) {
                                console.error("VendorUomMMapping failed on delete", err);
                              }
                            }}
                            title="Remove UOM"
                          >
                            −
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-4 border-t flex justify-end">
                <button
                  onClick={handleSave}
                  className="h-[34px] px-4 rounded-[6px] bg-[#2F5BFF] text-white text-[13px] font-semibold"
                >
                  Save UOM
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SelectUOMSidePanel;
